import {ApolloClient, ApolloLink, CombinedGraphQLErrors, gql, HttpLink, InMemoryCache} from '@apollo/client';
import {ConfigService} from '../config.service';
import {UserConfig} from '../../../../../backend/src/api/queries/user-config.query';
import {globalStateService} from '../../views/loading/loading';
import {createNetworkCapabilities, error, execute, NetworkedList, success} from '../../utils/data.utils';
import {Site} from '../../../../../backend/src/api/queries/site.query';
import {FilterParams, Report} from '../../../../../backend/src/api/queries/reports.query';
import {ReportData} from '../../../../../backend/src/api/mutations/report.mutation';
import {ErrorLink} from '@apollo/client/link/error';
import {RouterService} from '../router.service';
import {AccumulatedReport} from '../../../../../backend/src/api/queries/accumulated-reports.query';

export class DataService {
    public static async init(): Promise<DataService> {
        if (!DataService.initialized) {
            const customFetch = (uri, options) => {
                if (localStorage.getItem('id')) {
                    return fetch(`${ConfigService.get('API_URL')}?id=${localStorage.getItem('id')}`, options);
                }
                return fetch(ConfigService.get('API_URL') as string, options);
            };
            const errorLink = new ErrorLink(({ error }) => {
                if (CombinedGraphQLErrors.is(error)) {
                    error.errors.forEach(({ extensions, message }) => {
                        if (extensions.code === 'UNAUTHENTICATED') {
                            localStorage.removeItem('id');
                        }
                        if (extensions.code !== 'BAD_USER_INPUT') {
                            RouterService.activeGlobalError = message;
                        }
                    });
                } else {
                    RouterService.activeGlobalError = 'errors.network';
                }
                if (RouterService.activeGlobalError && RouterService.initialized) {
                    RouterService.navigate('error');
                }
            });
            const httpLink = new HttpLink({ fetch: customFetch });
            DataService.apolloClient = new ApolloClient({
                cache: new InMemoryCache(),
                link: ApolloLink.from([errorLink, httpLink])
            });
            await DataService.getUserConfig();
            return DataService;
        } else {
            console.log('Already initialized DataService!');
        }
    }

    private static initialized = false;

    private static apolloClient;

    public static async getUserConfig(): Promise<UserConfig> {
        globalStateService.set({
            app: {
                userConfig: execute('_fetch')
            }
        });
        let action;
        return DataService.apolloClient
            .query({
                fetchPolicy: 'no-cache',
                query: gql`
                    query userConfig {
                        userConfig {
                            isAdmin,
                            siteCode,
                            editableWeeks {
                                week,
                                year,
                                until
                            },
                            availableFilterParams {
                                year,
                                quarter,
                                month,
                                weekStart,
                                weekEnd,
                                site
                                cot
                                rtm
                                sapId
                                zip
                                place,
                                address
                            }
                        }
                    }
                `
            })
            .then(result => {
                if (result.error || result.errors) {
                    action = error('_fetch', 'Error fetching user config!');
                    return null;
                } else {
                    if (result.data.userConfig.isAdmin) {
                        result.data.userConfig.siteCode = result.data.userConfig.availableFilterParams.site[0];
                    }
                    action = success('_fetch', result.data.userConfig);
                    return result.data.userConfig;
                }
            })
            .catch(e => {
                action = error('_fetch', 'Error fetching user config!');
                return null;
            })
            .finally(() => {
                globalStateService.set({
                    app: {
                        userConfig: action
                    },
                    ...(action?.editableWeeks && Array.isArray(action?.editableWeeks) && action?.editableWeeks.length > 0) && {
                        components: {
                            list: {
                                reports: {
                                    filterParams: {
                                        year: action.editableWeeks[0].year
                                    }
                                }
                            }
                        }
                    }
                });
            });
    }

    public static async getSite(siteCode: number): Promise<Site> {
        globalStateService.set({
            components: {
                form: {
                    site: execute('_fetch'),
                    report1: createNetworkCapabilities([ '_add', '_fetch']),
                    report2: createNetworkCapabilities([ '_add', '_fetch'])
                }
            }
        });
        let action;
        return DataService.apolloClient
            .query({
                fetchPolicy: 'no-cache',
                query: gql`
                    query site($code: Int!) {
                        site(code: $code) {
                            code
                            company
                            address
                            zip
                            place
                            fuels {
                                super
                                diesel
                                ultimateDiesel
                                autogas
                                ultimateSuper
                                adblue
                                hvo
                            }
                        }
                    }
                `,
                variables: {
                    code: siteCode
                }
            })
            .then(result => {
                if (result.error || result.errors) {
                    action = error('_fetch', 'Error fetching site!');
                    return null;
                } else {
                    action = success('_fetch', result.data.site);
                    return result.data.site;
                }
            })
            .catch(e => {
                action = error('_fetch', 'Error fetching user config!');
                return null;
            })
            .finally(() => {
                globalStateService.set({
                    components: {
                        form: {
                            site: action
                        }
                    }
                });
            });
    }

    public static getPaginatedAccumulatedReports(filter: FilterParams, targetPage?: 'list', target?: 'reports'): Promise<NetworkedList<AccumulatedReport>> {
        globalStateService.set({
            components: {
                [targetPage]: {
                    [target]: execute('_fetch')
                }
            }
        });
        let action;
        return DataService.apolloClient
            .query({
                fetchPolicy: 'no-cache',
                query: gql`
                    query accumulatedReports($filter: FilterParams!) {
                        accumulatedReports(filter: $filter) {
                            data {
                                siteCode
                                sapId
                                zip
                                place
                                address
                                status {
                                    week
                                    year
                                    report
                                }                               
                            },
                            count                            
                        }
                    }
                `,
                variables: {
                    filter
                }
            })
            .then(result => {
                if (result.error || result.errors) {
                    action = error('_fetch', 'Error fetching reports!');
                    return null;
                } else {
                    let res = result.data.accumulatedReports;
                    action = success('_fetch', res);
                    return res;
                }
            })
            .catch(e => {
                action = error('_fetch', 'Error fetching reports!');
                return null;
            })
            .finally(() => {
                globalStateService.set({
                    components: {
                        [targetPage]: {
                            [target]: action
                        }
                    }
                });
            });
    }

    public static getPaginatedReports(filter: FilterParams, targetPage?: 'form' | 'detail', target?: 'reports'): Promise<NetworkedList<Report>> {
        if (targetPage && target) {
            globalStateService.set({
                components: {
                    [targetPage]: {
                        [target]: execute('_fetch')
                    }
                }
            });
        }
        let action;
        return DataService.apolloClient
            .query({
                fetchPolicy: 'no-cache',
                query: gql`
                    query reports($filter: FilterParams!) {
                        reports(filter: $filter) {
                            data {
                                siteCode
                                year
                                week
                                createdAt
                                superDevAbs
                                superDevRel
                                dieselDevAbs
                                dieselDevRel
                                ultimateDieselDevAbs
                                ultimateDieselDevRel
                                ultimateSuperDevAbs
                                ultimateSuperDevRel
                                autogasDevAbs
                                autogasDevRel
                                adblueDevAbs
                                adblueDevRel
                                hvoDevAbs
                                hvoDevRel
                                sapId
                                zip
                                place
                                address
                            },
                            count                            
                        }
                    }
                `,
                variables: {
                    filter
                }
            })
            .then(result => {
                if (result.error || result.errors) {
                    action = error('_fetch', 'Error fetching reports!');
                    return null;
                } else {
                    let res = result.data.reports;
                    action = success('_fetch', res);
                    return res;
                }
            })
            .catch(e => {
                action = error('_fetch', 'Error fetching reports!');
                return null;
            })
            .finally(() => {
                if (targetPage && target) {
                    globalStateService.set({
                        components: {
                            [targetPage]: {
                                [target]: action
                            }
                        }
                    });
                }
            });
    }
    
    public static async getReport(filter: FilterParams, targetPage: 'form', target: 'report1' | 'report2' ): Promise<Report[]> {
        globalStateService.set({
            components: {
                [targetPage]: {
                    [target]: execute('_fetch')
                }
            }
        });
        let action;
        return DataService.apolloClient
            .query({
                fetchPolicy: 'no-cache',
                query: gql`
                    query reports($filter: FilterParams!) {
                        reports(filter: $filter) {
                            data {
                                siteCode
                                year
                                week
                                createdAt
                                superDevAbs
                                superDevRel
                                dieselDevAbs
                                dieselDevRel
                                ultimateDieselDevAbs
                                ultimateDieselDevRel
                                ultimateSuperDevAbs
                                ultimateSuperDevRel
                                autogasDevAbs
                                autogasDevRel
                                adblueDevAbs
                                adblueDevRel
                                hvoDevAbs
                                hvoDevRel                         
                            }                           
                        }
                    }
                `,
                variables: {
                    filter
                }
            })
            .then(result => {
                if (result.error || result.errors) {
                    action = error('_fetch', 'Error fetching reports!');
                    return null;
                } else {
                    let res = result.data.reports.data.length > 0 ? result.data.reports.data[0] : {}
                    action = success('_fetch', res);
                    return res;
                }
            })
            .catch(e => {
                action = error('_fetch', 'Error fetching reports!');
                return null;
            })
            .finally(() => {
                globalStateService.set({
                    components: {
                        [targetPage]: {
                            [target]: {
                                _reducerMode: 'replace',
                                ...(targetPage === 'form' && target.startsWith('report')) && { _add: (globalStateService.state.components[targetPage][target] as any)._add },
                                ...action
                            }
                        }
                    }
                });
            });
    }

    public static async addReport(data: ReportData, reportField: 'report1' | 'report2'): Promise<Site> {
        globalStateService.set({
            components: {
                form: {
                    [reportField]: execute('_add')
                }
            }
        });
        let action;
        return DataService.apolloClient
            .mutate({
                mutation: gql`
                    mutation report($data: ReportData!) {
                        report(data: $data)
                    }
                `,
                variables: {
                    data
                }
            })
            .then(result => {
                if (result.error || result.errors) {
                    action = error('_add', 'errors.general');
                    return null;
                } else {
                    action = success('_add', result.data.site);
                    return result.data.report;
                }
            })
            .catch(e => {
                action = error('_add', e.message && e.message.startsWith('errors.') ? e.message : 'errors.general');
                return null;
            })
            .finally(() => {
                globalStateService.set({
                    components: {
                        form: {
                            [reportField]: action
                        }
                    }
                });
            });
    }

    public static exceedsDeviationThreshold(dev: number): boolean {
        let negativeDevThreshold = -Math.abs(+ConfigService.get('DEVIATION_THRESHOLD'));
        return (dev >= +ConfigService.get('DEVIATION_THRESHOLD') || dev <= negativeDevThreshold);
    }
}
